import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  Grid2,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
      background: "#EBF3FD",
    },
  },
};

const Step4 = ({
  sra,
  setSra,
  setStep,
  approvers,
  setApprovers,
  noOfApprovers,
  setNoOfApprovers,
  storedUser,
  approvalLimit,
  maxApprovalLimit,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const amount = Number(sra?.amount ?? 0);
  const limit = Number(approvalLimit ?? 0);
  const maxLimit = Number(maxApprovalLimit ?? 0);

  const requiresCMDTier = maxLimit > 0 && limit > 0 && amount >= maxLimit;
  const requiresCFOTier =
    !requiresCMDTier &&
    limit > 0 &&
    amount >= limit &&
    (maxLimit === 0 || amount < maxLimit);

  const getReservedEmails = (users) => {
    const cfos = users.filter((u) => u.userRoleName === "CFO" && u.isEnable);
    const cmds = users.filter((u) => u.userRoleName === "CMD" && u.isEnable);
    const reserved = new Set();
    if (requiresCMDTier) {
      cfos.forEach((u) => reserved.add(u.email));
      cmds.forEach((u) => reserved.add(u.email));
    } else if (requiresCFOTier) {
      cfos.forEach((u) => reserved.add(u.email));
    }
    return reserved;
  };

  const buildApproversArray = (current, users, count) => {
    const cfos = users.filter((u) => u.userRoleName === "CFO" && u.isEnable);
    const cmds = users.filter((u) => u.userRoleName === "CMD" && u.isEnable);
    const reservedEmails = getReservedEmails(users);

    const resized = Array.from({ length: count }, (_, i) => {
      const val = current[i] || "";
      return reservedEmails.has(val) ? "" : val;
    });

    if (requiresCMDTier && count >= 2) {
      resized[count - 1] = cmds.length === 1 ? cmds[0].email : "";
      resized[count - 2] = cfos.length === 1 ? cfos[0].email : "";
    } else if (requiresCFOTier && count >= 1) {
      resized[count - 1] = cfos.length === 1 ? cfos[0].email : "";
    }

    return resized;
  };

  useEffect(() => {
    const getOrgUsers = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/all?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setOrgUsers(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getOrgUsers();
  }, []);

  useEffect(() => {
    setApprovers((prev) => buildApproversArray(prev, orgUsers, noOfApprovers));
  }, [noOfApprovers]);

  useEffect(() => {
    if (noOfApprovers === 0) return;
    setApprovers((prev) => buildApproversArray(prev, orgUsers, noOfApprovers));
  }, [orgUsers]);

  useEffect(() => {
    const fetchDraft = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/draft/approver?draftSraId=${sra.draftSraId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );

        const responseApprovers = res.data
          .sort((a, b) => a.number - b.number)
          .map((item) => item.approver?.email || "");

        setApprovers(responseApprovers);
      } catch (error) {
        console.error("Failed to fetch draft:", error);
      }
    };

    fetchDraft();
  }, []);

  const getSlotOptions = (index) => {
    const isLastSlot = index === noOfApprovers - 1;
    const isSecondLastSlot = index === noOfApprovers - 2;
    const cfoUsers = orgUsers.filter((u) => u.userRoleName === "CFO" && u.isEnable);
    const cmdUsers = orgUsers.filter((u) => u.userRoleName === "CMD" && u.isEnable);

    if (requiresCMDTier && noOfApprovers >= 2) {
      if (isLastSlot) {
        return cmdUsers.filter(
          (u) => !approvers.some((email, i) => i !== index && email === u.email),
        );
      }
      if (isSecondLastSlot) {
        return cfoUsers.filter(
          (u) => !approvers.some((email, i) => i !== index && email === u.email),
        );
      }
    }

    if (requiresCFOTier && isLastSlot) {
      return cfoUsers.filter(
        (u) => !approvers.some((email, i) => i !== index && email === u.email),
      );
    }

    const reservedRoles = [
      ...(requiresCMDTier ? ["CFO", "CMD"] : []),
      ...(requiresCFOTier ? ["CFO"] : []),
    ];
    const excludedRoles = [
      "BUYER",
      "FINANCE_PROCESSOR",
      "SUPERUSER",
      "ACCOUNT_PAYABLE_TEAM",
      ...reservedRoles,
    ];

    return orgUsers.filter(
      (user) =>
        !excludedRoles.includes(user.userRoleName) &&
        !approvers.some((email, i) => i !== index && email === user.email) &&
        user.isEnable,
    );
  };

  const getSlotLabel = (index) => {
    const isLastSlot = index === noOfApprovers - 1;
    const isSecondLastSlot = index === noOfApprovers - 2;

    if (requiresCMDTier && noOfApprovers >= 2) {
      if (isLastSlot)
        return <span style={{ color: "#EA981F", marginLeft: 4 }}>(CMD required)</span>;
      if (isSecondLastSlot)
        return <span style={{ color: "#EA981F", marginLeft: 4 }}>(CFO required)</span>;
    }
    if (requiresCFOTier && isLastSlot) {
      return <span style={{ color: "#EA981F", marginLeft: 4 }}>(CFO required)</span>;
    }
    return null;
  };

  const getPlaceholder = (index) => {
    const isLastSlot = index === noOfApprovers - 1;
    const isSecondLastSlot = index === noOfApprovers - 2;

    if (requiresCMDTier && noOfApprovers >= 2) {
      if (isLastSlot) return "Select CMD";
      if (isSecondLastSlot) return "Select CFO";
    }
    if (requiresCFOTier && isLastSlot) return "Select CFO";
    return "Select Approver";
  };

  const validateForm = () => {
    const newErrors = {};

    if (noOfApprovers === 0) {
      newErrors.noOfApprovers = "Please select at least one approver.";
    }
    if (requiresCMDTier && noOfApprovers < 2) {
      newErrors.noOfApprovers =
        "This amount requires at least 2 approvers (CFO + CMD).";
    }

    approvers.slice(0, noOfApprovers).forEach((approver) => {
      if (!approver || approver.trim?.() === "") {
        newErrors.approver =
          "Please select all approvers as per the selected number.";
      }
    });

    if (!newErrors.approver) {
      if (requiresCMDTier && noOfApprovers >= 2) {
        const lastUser = orgUsers.find(
          (u) => u.email === approvers[noOfApprovers - 1],
        );
        const secondLastUser = orgUsers.find(
          (u) => u.email === approvers[noOfApprovers - 2],
        );
        if (!lastUser || lastUser.userRoleName !== "CMD") {
          newErrors.approver = "The last approver must be a CMD for this amount.";
        } else if (!secondLastUser || secondLastUser.userRoleName !== "CFO") {
          newErrors.approver =
            "The second-last approver must be a CFO for this amount.";
        }
      } else if (requiresCFOTier && noOfApprovers > 0) {
        const lastUser = orgUsers.find(
          (u) => u.email === approvers[noOfApprovers - 1],
        );
        if (!lastUser || lastUser.userRoleName !== "CFO") {
          newErrors.approver = "The last approver must be a CFO for this amount.";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApproversChange = (index, value) => {
    const updated = [...approvers];
    updated[index] = value;
    setApprovers(updated);
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      const indexedData = approvers.map((item, index) => ({
        ...item,
        username: item,
        number: index + 1,
        numberOfApprovers: noOfApprovers,
      }));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft/approver?draftSraId=${sra.draftSraId}`,
        indexedData,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => setStep((prev) => prev - 1);

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Service Order Approval Form</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "80%" }}></span>
      </div>

      <div className="dashboard-container">
        <Typography className="form-title">Select Approvers</Typography>
        <label style={{ fontWeight: "bold" }}>Number of Approvers:</label>
        <select
          className={`${errors.noOfApprovers && "input-error"}`}
          value={noOfApprovers}
          onChange={(e) => {
            setNoOfApprovers(Number(e.target.value));
            setErrors({ ...errors, noOfApprovers: "" });
          }}
          style={{ marginLeft: "10px", padding: "5px", width: "200px" }}
        >
          <option value="">Select</option>
          {[1, 2, 3, 4]
            .filter((n) => !requiresCMDTier || n >= 2)
            .map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
        </select>
        {errors.noOfApprovers && (
          <p className="error">{errors.noOfApprovers}</p>
        )}
        <Grid2 container spacing={2}>
          {Array.from({ length: noOfApprovers }, (_, index) => {
            const slotOptions = getSlotOptions(index);

            return (
              <Grid2 key={index} size={{ xs: 12, sm: 4 }}>
                <label>
                  Approver {index + 1}
                  {getSlotLabel(index)}:
                </label>
                <Select
                  value={approvers[index] || ""}
                  onChange={(e) => handleApproversChange(index, e.target.value)}
                  displayEmpty
                  IconComponent={(props) => (
                    <KeyboardArrowDownIcon {...props} fontSize="small" />
                  )}
                  MenuProps={MenuProps}
                  sx={{
                    display: "flex",
                    borderRadius: "7px",
                    padding: "8px 7px",
                    fontSize: "14px",
                    margin: "7px 0 20px 0",
                    "& .MuiSelect-select": {
                      fontFamily: "Inter !important",
                      padding: "5px",
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {getPlaceholder(index)}
                  </MenuItem>
                  {slotOptions.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                      {user.name} ({user.userRoleName.replace(/_/g, " ")})
                    </MenuItem>
                  ))}
                </Select>
              </Grid2>
            );
          })}
        </Grid2>
        {errors.approver && <p className="error">{errors.approver}</p>}
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={handleNext}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step4;
